/**
 * Histórico de alterações do agente: quem mudou, quando, o quê, e o valor antes e depois.
 * Do mais recente ao mais antigo, como a API devolve.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button } from '@mui/material';
import { History as HistoryIcon } from '@mui/icons-material';
import { DataTable, SectionCard, type DataTableColumn } from '../../../components';
import {
  hazardousAgentService,
  type HazardousAgentChange,
} from '../../../services/hazardousAgentService';
import { ACTION_LABELS, FIELD_LABELS, readableValue } from '../degreeLabels';

const PAGE_SIZE = 10;

const columns: DataTableColumn<HazardousAgentChange>[] = [
  {
    id: 'createdAt',
    label: 'Quando',
    render: (change) => new Date(change.createdAt).toLocaleString('pt-BR'),
  },
  { id: 'userName', label: 'Quem', primary: true, render: (change) => change.userName },
  { id: 'action', label: 'Ação', render: (change) => ACTION_LABELS[change.action] ?? change.action },
  { id: 'field', label: 'O que mudou', render: (change) => FIELD_LABELS[change.field] ?? change.field },
  { id: 'oldValue', label: 'Antes', render: (change) => readableValue(change.field, change.oldValue) },
  { id: 'newValue', label: 'Depois', render: (change) => readableValue(change.field, change.newValue) },
];

interface Props {
  agentId: number;
  /** Muda a cada alteração gravada na tela, para a lista buscar de novo. */
  refreshKey: number;
}

export const AgentHistorySection: React.FC<Props> = ({ agentId, refreshKey }) => {
  const [changes, setChanges] = useState<HazardousAgentChange[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const requestSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    try {
      setLoading(true);
      setFailed(false);
      const response = await hazardousAgentService.history(agentId, page + 1, PAGE_SIZE);
      if (seq !== requestSeq.current) return;
      setChanges(response.data);
      setTotal(response.pagination.total);
    } catch {
      if (seq === requestSeq.current) setFailed(true);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [agentId, page]);

  // Depois de uma alteração a lista volta à primeira página, onde está o registro mais recente.
  // Fora da primeira, é a troca de página que dispara a busca; na primeira, a busca é direta.
  const seenRefreshKey = useRef(refreshKey);
  useEffect(() => {
    if (seenRefreshKey.current !== refreshKey) {
      seenRefreshKey.current = refreshKey;
      if (page !== 0) {
        setPage(0);
        return;
      }
    }
    load();
  }, [load, refreshKey, page]);

  return (
    <SectionCard title="Histórico de alterações" icon={<HistoryIcon />} badge={`${total}`}>
      {failed && !loading ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={load}>
              Tentar de novo
            </Button>
          }
        >
          Não foi possível carregar o histórico.
        </Alert>
      ) : (
        <DataTable
          embedded
          columns={columns}
          rows={changes}
          getRowId={(change) => change.id}
          loading={loading}
          emptyState={{
            title: 'Nenhuma alteração registrada',
            description:
              'Quando alguém mudar o grau, o anexo, os limites ou a situação deste agente, o registro aparece aqui.',
          }}
          pagination={{
            page,
            rowsPerPage: PAGE_SIZE,
            totalCount: total,
            onPageChange: setPage,
            onRowsPerPageChange: () => undefined,
            rowsPerPageOptions: [PAGE_SIZE],
          }}
        />
      )}
    </SectionCard>
  );
};

export default AgentHistorySection;
